import { type VariantProps, cva } from 'class-variance-authority';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from './cn.js';

/**
 * Bouton. Voir docs/DESIGN.md §3.
 *
 * Deux règles non évidentes :
 *  - l'anneau de focus n'est JAMAIS supprimé, seulement restylé ;
 *  - en état `loading`, le libellé reste dans le flux (opacité 0) pour que
 *    le bouton ne change pas de largeur — un bouton qui rétrécit sous le
 *    curseur provoque des clics à côté.
 */
const button = cva(
  'inline-flex items-center justify-center gap-2 rounded-md font-medium ' +
    'transition-colors duration-[120ms] ease-out ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg ' +
    'disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-fg hover:bg-accent-hover',
        secondary: 'bg-surface-raised text-text border border-border hover:border-border-strong',
        ghost: 'text-text-muted hover:bg-surface-raised hover:text-text',
        danger: 'bg-danger text-white hover:opacity-90',
      },
      size: {
        // 44px de haut minimum sur les tailles md et lg : cible tactile.
        sm: 'h-9 px-3 text-sm',
        md: 'h-11 px-4 text-sm',
        lg: 'h-12 px-6 text-base',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  loading?: boolean;
  children: ReactNode;
}

export function Button({
  variant,
  size,
  loading = false,
  disabled,
  className,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      type="button"
      className={cn(button({ variant, size }), 'relative', className)}
      disabled={disabled === true || loading}
      aria-busy={loading}
      {...props}
    >
      <span className={loading ? 'invisible' : 'contents'}>{children}</span>
      {loading ? (
        <span className="absolute inset-0 grid place-items-center" aria-hidden="true">
          <Spinner />
        </span>
      ) : null}
    </button>
  );
}

function Spinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin motion-reduce:animate-none"
      viewBox="0 0 24 24"
      fill="none"
      // Purement décoratif : l'état de chargement est déjà annoncé par
      // `aria-busy` sur le bouton. Un titre ici ferait doublon à l'oral.
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path
        d="M12 2a10 10 0 0 1 10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
